import * as THREE from "three";
import { makeToon, toonify, TOON_GRADIENT } from "./toon.ts";
import { applyFlatten } from "./flatten.ts";
import { mapFeatureBase, mapBrushDelta, noiseOff, noiseAmp, segDist } from "./map/layers.ts";
import { MAP_SIZE, WATER_LEVEL } from "./map/active.ts"; // live bindings — the ACTIVE map's scalars (plan 21)
import type { RoadStyle } from "./content/schemas.ts";

// ---------------------------------------------------------------------------
// #2 Procedural terrain: value-noise heightmap + height/slope-based biomes.
// heightAt() is the shared source of truth — the terrain mesh, deco placement,
// and the player all sample it so everything sits on the same surface.
//
// Per-map size + water (plan 21): WORLD extent and the waterline are the
// ACTIVE map's (src/map/active.ts live bindings) — everything here derives
// them PER CALL (functions, not module consts), because the client learns its
// real map only at `spawn`, long after module init.
// ---------------------------------------------------------------------------

export { WATER_LEVEL }; // re-export (live) — half the codebase reads the waterline from here
const AMP = 8; // vertical amplitude of the hills

// --- value noise ---
/** Cheap deterministic 0..1 hash — the value-noise grid PRNG, also reused by the
 *  edge-preview strips (src/edge/strip.ts) for per-vertex jitter so their tonal
 *  variation stays in sync with the real terrain they continue. */
export function hash(x: number, z: number): number {
  const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function valueNoise(x: number, z: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  const xf = x - xi, zf = z - zi;
  const u = smooth(xf), v = smooth(zf);
  return lerp(
    lerp(hash(xi, zi), hash(xi + 1, zi), u),
    lerp(hash(xi, zi + 1), hash(xi + 1, zi + 1), u),
    v,
  );
}
/** Fractal noise (stacked octaves) → 0..1. Exported for the server's per-map
 *  TerrainCtx (src/server/maps.ts), which composes the same math per map. */
export function fbm(x: number, z: number): number {
  let f = 0, amp = 0.5, freq = 0.02, norm = 0;
  for (let o = 0; o < 4; o++) {
    f += valueNoise(x * freq, z * freq) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return f / norm;
}

// --- shoreline carving ---
// The water is a flat plane at WATER_LEVEL; water shows wherever terrain dips
// below it. Raw fbm hills are gentle, so big near-level areas sit *just* under
// the line and water sheets out in shallow fuzzy tendrils ("bleeding"). carveShore
// remaps heights around the waterline to (a) drain margins shallower than DRAIN
// below the line — lifting them onto land so the footprint tightens to genuine
// low spots — and (b) steepen the banks, so water bodies read with crisp edges.
// Continuous at the band edges (deep basins below, real land above are untouched).
const DRAIN = 0.85; // depth below WATER_LEVEL still drained to land
const SHORE_BAND = 2.0; // half-width of the remapped band around the pivot
/** Shore remap around `waterLevel` (defaults to the ACTIVE map's — exported so
 *  the server's per-map TerrainCtx can carve against ITS water level). The
 *  pivot derives per call: the waterline is a per-map live value. */
export function carveShore(h: number, waterLevel: number = WATER_LEVEL): number {
  const pivot = waterLevel - DRAIN;
  const d = h - pivot;
  if (d <= -SHORE_BAND || d >= SHORE_BAND) return h; // deep basin / high land: as-is
  const t = d / SHORE_BAND; // -1..1
  const s = Math.sign(t) * Math.pow(Math.abs(t), 0.6); // contrast: push away from the pivot
  return pivot + s * SHORE_BAND;
}

/** World-space ground elevation at (x, z). Composes the plan-20 map layers
 *  (src/map/layers.ts) over the noise base:
 *    noise → features (PRE-carve: basins get natural banks) → carveShore →
 *    brush (POST-carve: hand sculpting is WYSIWYG) → flatten zones/pads.
 *  With an empty map layer (no features/brush, offset [0,0]) this is
 *  byte-identical to the classic pipeline — pinned by
 *  src/map/migration-parity.test.ts. */
/** The raw noise layer at (x,z) with a domain offset — the pre-feature base.
 *  Exported for the per-map TerrainCtx (src/map/terrain-ctx.ts). */
export function noiseBase(x: number, z: number, ox = 0, oz = 0, amp = 1): number {
  return (fbm(x + ox, z + oz) - 0.42) * AMP * 2 * amp;
}

export function heightAt(x: number, z: number): number {
  const base = mapFeatureBase(x, z, noiseBase(x, z, noiseOff.x, noiseOff.z, noiseAmp.v));
  const h = carveShore(base) + mapBrushDelta(x, z);
  // Level out building footprints / authored map flats (see flatten.ts).
  return applyFlatten(x, z, h);
}

// --- biome coloring (height + slope) ---
const SAND = new THREE.Color(0xc2b280);
const GRASS = new THREE.Color(0x4a7a3a);
const FOREST = new THREE.Color(0x37602c);
const ROCK = new THREE.Color(0x6f6b61);
const SNOW = new THREE.Color(0xeef3f6);

// Dirt roads paint the terrain itself (same mesh / normals as grass) so they
// read as worn earth, not a floating tile path. setTerrainRoadPaint before
// buildTerrain / chunk rebuild; city roads still use path.ts cobble meshes.
// Leaf-safe: only waypoints/width/style — no import of world.ts.

export type TerrainRoadPaint = {
  waypoints: [number, number][];
  width?: number;
  style?: RoadStyle;
};

/** Soft edge feather (world units) so dirt blends into grass — no hard curb.
 *  Exported so studio dirties chunks with the same pad when paint changes. */
export const DIRT_FEATHER = 0.85;

/** Hydrated dirt road: AABB reject before per-segment distance. */
type DirtRoad = {
  waypoints: [number, number][];
  half: number;
  minX: number; maxX: number; minZ: number; maxZ: number;
};
let dirtRoads: DirtRoad[] = [];

/** Call before building terrain so dirt-style roads tint the ground. */
export function setTerrainRoadPaint(roads: TerrainRoadPaint[]): void {
  dirtRoads = [];
  for (const road of roads) {
    if ((road.style ?? "city") !== "dirt") continue;
    const wp = road.waypoints;
    if (wp.length < 2) continue;
    const half = (road.width ?? 3.0) / 2;
    const pad = half + DIRT_FEATHER;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [x, z] of wp) {
      minX = Math.min(minX, x - pad);
      maxX = Math.max(maxX, x + pad);
      minZ = Math.min(minZ, z - pad);
      maxZ = Math.max(maxZ, z + pad);
    }
    dirtRoads.push({ waypoints: wp, half, minX, maxX, minZ, maxZ });
  }
}

/** Light sandy dirt — same family as beach sand, a bit browner. */
const DIRT_ROAD = new THREE.Color(0xc9a87a);
/** Scratch for per-vertex dirt tint (avoid Color.clone() in the hot loop). */
const _dirtScratch = new THREE.Color();

/** 0 = no dirt road, 1 = fully on a dirt road centreline. Soft-edged. */
function dirtRoadBlend(x: number, z: number): number {
  if (!dirtRoads.length) return 0;
  let best = 0;
  for (const road of dirtRoads) {
    if (x < road.minX || x > road.maxX || z < road.minZ || z > road.maxZ) continue;
    const wp = road.waypoints;
    const half = road.half;
    for (let i = 0; i < wp.length - 1; i++) {
      const d = segDist(x, z, wp[i][0], wp[i][1], wp[i + 1][0], wp[i + 1][1]).d;
      let w = 0;
      if (d <= half) w = 1;
      else if (d < half + DIRT_FEATHER) w = 1 - (d - half) / DIRT_FEATHER;
      if (w > best) best = w;
      if (best >= 1) return 1;
    }
  }
  return best;
}

/** Biome color against an explicit waterline — exported for the edge-preview
 *  strips (src/edge/strip.ts), which paint a NEIGHBOR map's terrain and must
 *  judge "beach" against ITS water level, not the active map's. */
export function biomeColorFor(
  y: number,
  slopeUp: number,
  jitter: number,
  waterLevel: number,
): THREE.Color {
  let c: THREE.Color;
  if (y < waterLevel + 0.5) c = SAND.clone();
  else if (slopeUp < 0.72) c = ROCK.clone(); // steep faces are rocky
  else if (y > 6.5) c = SNOW.clone();
  else if (y > 4) c = FOREST.clone();
  else c = GRASS.clone();
  // subtle per-vertex tonal variation so flat facets don't look uniform
  c.offsetHSL(0, 0, (jitter - 0.5) * 0.06);
  return c;
}

function biomeColor(y: number, slopeUp: number, jitter: number): THREE.Color {
  return biomeColorFor(y, slopeUp, jitter, WATER_LEVEL);
}

/** Flat top-down color for the minimap at (x, z) — height-based biome, no slope. */
export function mapColorAt(x: number, z: number): string {
  const y = heightAt(x, z);
  let c: THREE.Color;
  if (y < WATER_LEVEL) c = new THREE.Color(0x2f5d8a);      // deep water
  else if (y < WATER_LEVEL + 0.6) c = SAND.clone();        // beach
  else if (y > 6.5) c = SNOW.clone();
  else if (y > 4) c = FOREST.clone();
  else c = GRASS.clone();
  return `#${c.getHexString()}`;
}

// ~2.5 world-units per segment: at size 1080 that's ~1.1M non-indexed verts
// total — the 1.5u density used at 360 would be ~3M and blow memory/build time
// on both the client and the headless server. fbm feature wavelengths (6-50u)
// stay well-sampled; flatten feathers (9u) survive. (2.5 also = the map
// brush's default cell size, so sculpt deltas land on verts.)
//
// Chunk math is PER-CALL over a size (default: the active map's) — chunk side
// stays ~135u whatever the map size, so 1080 → 8×8, 2160 → 16×16.
/** Chunk-grid dimension for a map of side `size` (8 at 1080, 16 at 2160). */
export function terrainChunks(size: number = MAP_SIZE): number {
  return Math.max(1, Math.round(size / 135));
}
/** Chunk side length in world units (~135). */
export function chunkW(size: number = MAP_SIZE): number {
  return size / terrainChunks(size);
}

/** Chunk grid index containing world (x,z), clamped into the grid — the studio
 *  world editor maps sculpted brush cells to dirty chunks with this. */
export function chunkIndexAt(x: number, z: number, size: number = MAP_SIZE): [number, number] {
  const n = terrainChunks(size), cw = chunkW(size);
  const c = (v: number) =>
    Math.min(n - 1, Math.max(0, Math.floor((v + size / 2) / cw)));
  return [c(x), c(z)];
}

// ONE material for every chunk (lazy so headless boots don't pay for it until a
// build). Shared — never dispose it when swapping a rebuilt chunk's geometry.
let terrainMat: THREE.MeshToonMaterial | null = null;

/** Build ONE terrain chunk mesh (grid cell ix,iz — see terrainChunks()). The
 *  studio world editor rebuilds single chunks while sculpting; buildTerrain
 *  assembles the full grid from the same call. Chunk seams stay watertight
 *  because boundary verts sample heightAt directly, and faceted normals are
 *  per-triangle (non-indexed) so shading has no cross-chunk dependency. */
export function buildTerrainChunk(ix: number, iz: number, size: number = MAP_SIZE): THREE.Mesh {
  const cw = chunkW(size);
  const seg = Math.max(1, Math.round(cw / 2.5));
  terrainMat ??= makeToon({ vertexColors: true });

  const ox = -size / 2 + (ix + 0.5) * cw;
  const oz = -size / 2 + (iz + 0.5) * cw;
  const plane = new THREE.PlaneGeometry(cw, cw, seg, seg);
  plane.rotateX(-Math.PI / 2); // lay flat: vertices now in world XZ, y up
  plane.translate(ox, 0, oz); // bake the chunk's world position into the verts

  const ppos = plane.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < ppos.count; i++) {
    ppos.setY(i, heightAt(ppos.getX(i), ppos.getZ(i)));
  }

  // Toon material can't flat-shade, so bake faceted normals: split shared
  // vertices then recompute → one normal per triangle. Color the split mesh.
  const geo = plane.toNonIndexed();
  geo.computeVertexNormals();
  plane.dispose(); // the indexed intermediate never reaches the GPU, but be tidy

  const pos = geo.attributes.position as THREE.BufferAttribute;
  const normal = geo.attributes.normal as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    const c = biomeColor(
      pos.getY(i),
      normal.getY(i),
      hash(x * 3.1, z * 1.7),
    );
    // Dirt roads: lerp the biome toward worn earth IN PLACE on the terrain
    // mesh — no second geometry, so no squares / z-fight / floating plates.
    const dirt = dirtRoadBlend(x, z);
    if (dirt > 0) {
      _dirtScratch.copy(DIRT_ROAD);
      // Same subtle per-vertex tonal variation as biomes.
      _dirtScratch.offsetHSL(0, 0, (hash(x * 5.3, z * 2.9) - 0.5) * 0.06);
      c.lerp(_dirtScratch, dirt);
    }
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(geo, terrainMat);
  mesh.receiveShadow = true;
  // The ground does NOT cast shadows: hills self-shadowing is invisible at
  // this camera angle, and casting rendered the whole terrain a second time
  // into the shadow map every frame. Decos/structures still shade onto it.
  mesh.castShadow = false;
  return mesh;
}

/** Build the displaced, vertex-colored terrain as a grid of chunk meshes.
 *  Chunking (Phase C) is what makes the ~1M-vert map affordable per frame: each
 *  chunk has its own tight bounding sphere, so the camera's frustum + far plane
 *  (CAM_FAR sits just past full fog) cull everything not actually on screen —
 *  only a handful of nearby chunks ever render. Same vertices, same look. */
export function buildTerrain(size: number = MAP_SIZE): THREE.Group {
  const group = new THREE.Group();
  group.name = "terrain";
  const n = terrainChunks(size);
  for (let ix = 0; ix < n; ix++) {
    for (let iz = 0; iz < n; iz++) {
      group.add(buildTerrainChunk(ix, iz, size));
    }
  }
  return group;
}

/** Shared ripple clock — every water material's vertex shader reads this ONE
 *  uniform (the SWAY_TIME pattern); `rippleWater` bumps it. Waters that never
 *  bump it (studio stage, edge strips) simply rest flat, as before. */
export const WATER_TIME = { value: 0 };
const WAVE_A = 0.07;

/** The water material: GPU ripple + derivative facet normals. Built the sway
 *  way — its own onBeforeCompile FIRST, then toonify composes the rim over it. */
function makeWaterMat(): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({
    color: 0x3a78b5,
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    gradientMap: TOON_GRADIENT,
  });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = WATER_TIME;
    // The exact wave field the old CPU loop wrote: each term is 0..A (never
    // negative), so the surface only crests UPWARD from the resting plane — a
    // shallow basin covered at rest stays covered (the trough never sinks
    // below the floor to flicker in near-waterline depressions). Vertices
    // carry world XZ (baked per chunk in buildWater), so the field stays
    // phase-continuous across chunk seams.
    shader.vertexShader =
      "uniform float uTime;\n" +
      shader.vertexShader.replace(
        "#include <begin_vertex>",
        "#include <begin_vertex>\n" +
          `\ttransformed.y += ( sin( transformed.x * 0.3 + uTime ) * 0.5 + 0.5 ) * ${WAVE_A}` +
          ` + ( cos( transformed.z * 0.35 + uTime * 1.3 ) * 0.5 + 0.5 ) * ${WAVE_A};`,
      );
  };
  m.customProgramCacheKey = () => "waterripple1";
  // MeshToonMaterial has no flatShading param, but the FLAT_SHADED define flips
  // three's normal_fragment_begin to dFdx/dFdy of the view position — and on a
  // vertex-displaced, linearly-interpolated triangle that IS the face normal:
  // the same faceted look the old per-tick computeVertexNormals produced.
  m.defines = { FLAT_SHADED: "" };
  // Half rim: the bright transparent plane over-glows at full strength.
  toonify(m, { rim: 0.5 });
  // Per-build material (NOT the toonMat cache) — a map swap disposes it.
  m.userData.owned = true;
  return m;
}

/** Build the animated water surface — CHUNKED like the terrain: one whole-map
 *  plane has a whole-map bounding sphere and can never be frustum-culled, so
 *  every off-screen stretch of a big map's water still rasterized each frame.
 *  Tiles cull individually; total vertex density matches the old single plane
 *  at every map size. depthWrite stays false so the surface doesn't OCCLUDE
 *  what's just beneath it — fish shadows (src/fish.ts) sit a hair under the
 *  plane and must show through the translucent water while still being hidden
 *  by SOLID geometry (piers, banks), which the shadow's own depthTest handles. */
export function buildWater(size: number = MAP_SIZE, cullDry = false): THREE.Group {
  const group = new THREE.Group();
  group.name = "water";
  const n = terrainChunks(size);
  const cw = chunkW(size);
  // The old plane spread min(96, size/4) segments across the whole map —
  // divide them among the chunks so the facet size is unchanged.
  const seg = Math.max(1, Math.round(Math.min(96, Math.round(size / 4)) / n));
  const mat = makeWaterMat();
  // Dry-tile cull (game path only — buildWorld passes cullDry): a tile whose
  // terrain never dips near the waterline can't show water, so skip it at
  // build and a one-lake map keeps ~a handful of tiles instead of n². Sampled
  // on a ~4u grid with a generous +2u keep margin (shore carve eases terrain
  // toward the waterline over several units, so river/lake edges always trip
  // it): a false KEEP is just the old behavior (an invisible tile), a false
  // SKIP would be a hole in a lake. The studio world editor passes false —
  // it moves the group's y on the water-level slider and sculpts basins live,
  // so its grid must stay complete (WYSIWYG).
  const wet = (ox: number, oz: number): boolean => {
    const half = cw / 2, step = cw / 32;
    for (let x = ox - half; x <= ox + half + 1e-6; x += step)
      for (let z = oz - half; z <= oz + half + 1e-6; z += step)
        if (heightAt(x, z) < WATER_LEVEL + 2) return true;
    return false;
  };
  for (let ix = 0; ix < n; ix++) {
    for (let iz = 0; iz < n; iz++) {
      const ox = -size / 2 + (ix + 0.5) * cw;
      const oz = -size / 2 + (iz + 0.5) * cw;
      if (cullDry && !wet(ox, oz)) continue;
      // Indexed is fine now (fewer verts): facet normals come from fragment
      // derivatives, not per-vertex recomputes. World XZ bakes into the verts
      // (the terrain-chunk recipe) so the shader's wave field is seamless.
      const geo = new THREE.PlaneGeometry(cw, cw, seg, seg).rotateX(-Math.PI / 2);
      geo.translate(ox, 0, oz);
      group.add(new THREE.Mesh(geo, mat));
    }
  }
  group.position.y = WATER_LEVEL;
  return group;
}

/** Advance the shared ripple clock — the wave itself lives in the water
 *  material's vertex shader (the old version rewrote every vertex and
 *  recomputed every normal on the CPU at 20Hz; this is one uniform write, so
 *  callers can run it every frame and the motion is smoother too). */
export function rippleWater(t: number): void {
  WATER_TIME.value = t;
}
